import { Signal } from '@angular/core';
import { AbstractControl, FormGroup, FormGroupDirective, ValidationErrors } from '@angular/forms';
import dayjs from 'dayjs';

export class QuickForecastValidators {
  static customStartMilestone(formGroup: FormGroup) {
    return (control: AbstractControl): null | ValidationErrors => {
      const customEndMilestone = formGroup.get('customEndMilestone');

      if (!customEndMilestone?.value || !control.value) return null;

      const isBefore = dayjs(control.value).isBefore(customEndMilestone.value);
      const isSame = dayjs(control.value).isSame(customEndMilestone.value);

      if (!isBefore && isSame) {
        return null;
      }

      return isBefore ? null : { endMilestone: true };
    };
  }

  static isCustomDriverFormValid(form: FormGroup): boolean {
    return !!form.get('customCurve')?.valid;
  }

  static isDateInInclusiveRange(minDate: string, maxDate: string) {
    return (control: AbstractControl): null | ValidationErrors => {
      if (!control.value || !dayjs(control.value).isValid) return null;

      return dayjs(control.value).isSameOrAfter(minDate) &&
        dayjs(control.value).isSameOrBefore(maxDate)
        ? null
        : { trialTimelinePeriod: true };
    };
  }

  static isPatientDriverFormValid(form: FormGroup): boolean {
    return !!form.get('patientCurve')?.valid;
  }

  static isSiteDriverFormValid(form: FormGroup): boolean {
    const { siteCurve, siteDriverPeriod } = form.controls;

    siteCurve.updateValueAndValidity();

    return siteDriverPeriod.valid && siteCurve.valid;
  }

  static isTimeDriverFormValid(form: FormGroup, isAnyTimelineMilestoneHasValue: boolean): boolean {
    const {
      customEndMilestone,
      customStartMilestone,
      endMilestone,
      milestone,
      requiredTimeValidation,
      startMilestone,
      timeMethod,
    } = form.controls;

    if (!isAnyTimelineMilestoneHasValue) {
      requiredTimeValidation.setErrors({ requiredQuickForecast: true });

      if (!timeMethod.value) {
        timeMethod.setErrors({ requiredQuickForecast: true });
      }

      return false;
    }

    let isFormValid = true;

    if (!timeMethod.value) {
      timeMethod.setErrors({ requiredQuickForecast: true });
      isFormValid = false;
    }

    const allMilestoneFields = [
      customEndMilestone,
      customStartMilestone,
      startMilestone,
      endMilestone,
    ];

    if (milestone.value === 'custom' && allMilestoneFields.every((field) => !field.value)) {
      allMilestoneFields.forEach((field) => field.setErrors({ requiredQuickForecast: true }));
      isFormValid = false;
    }

    if (customStartMilestone.value && !customEndMilestone.value) {
      customEndMilestone.setErrors({ requiredQuickForecast: true });
      isFormValid = false;
    }

    if (customEndMilestone.value && !customStartMilestone.value) {
      customStartMilestone.setErrors({ requiredQuickForecast: true });
      isFormValid = false;
    }

    if (endMilestone.value && !startMilestone.value) {
      startMilestone.setErrors({ requiredQuickForecast: true });
      isFormValid = false;
    }

    if (startMilestone.value && !endMilestone.value) {
      endMilestone.setErrors({ requiredQuickForecast: true });
      isFormValid = false;
    }

    return (
      isFormValid &&
      !customStartMilestone.invalid &&
      !customEndMilestone.invalid &&
      !startMilestone.invalid &&
      !endMilestone.invalid
    );
  }

  static required(control: AbstractControl): null | ValidationErrors {
    return control.value ? null : { requiredQuickForecast: true };
  }

  static siteCurveBeforePeriod(
    periodControl: AbstractControl,
    formGroup: Signal<FormGroupDirective | undefined>
  ) {
    return (siteCurveControl: AbstractControl): null | ValidationErrors => {
      const periodSelected = !!periodControl.value;

      if (formGroup()?.submitted && !periodSelected && !siteCurveControl.touched) {
        return null;
      }

      return periodSelected ? null : { siteCurveBeforePeriod: true };
    };
  }

  static startMilestone(formGroup: FormGroup) {
    return (startMilestone: AbstractControl): null | ValidationErrors => {
      const endMilestone = formGroup.get('endMilestone');

      if (!endMilestone?.value || !startMilestone?.value) return null;

      const isBefore = dayjs(startMilestone.value?.contractStartDate).isBefore(
        endMilestone.value?.contractEndDate
      );
      const isSame = dayjs(startMilestone.value?.contractStartDate).isSame(
        endMilestone.value?.contractEndDate
      );

      if (!isBefore && isSame) {
        return null;
      }

      return isBefore ? null : { endMilestone: true };
    };
  }
}
